import { z } from 'zod';
/**
 * The append-only audit ledger's vocabulary (REQUIREMENTS §2.4).
 *
 * `transactions` is write-once: no update, no delete, for anyone, including
 * admins and including through the Admin SDK. Rules deny the client outright;
 * the server side is a code-review rule, because the Admin SDK cannot be
 * constrained by a rules file. Every entry is written with a server timestamp
 * and the acting uid.
 */
export const transactionTypeSchema = z.enum([
    'issue',
    'sign',
    'reject',
    /** The storeroom changed an unsigned form's lines (owner, 2026-09-13); before/after carry both line sets. */
    'form_edited',
    /**
     * Not in §2.4's list: the issuer withdrawing an unsigned form is a
     * different act from the soldier rejecting it, and the ledger should say
     * which happened.
     */
    'cancel',
    'return',
    'transfer_initiate',
    /** Someone above everyone involved signed the transfer off (owner, 2026-09-21); custody moves in the same transaction. */
    'transfer_approve',
    /** The same person refused it after the receiver had signed; the holder keeps the item. */
    'transfer_decline',
    /** The receiver signed and the approval landed; custody moved (§3.2 as revised 2026-09-21). */
    'transfer_complete',
    /**
     * Admin/csm completed a transfer without the receiver's signature,
     * by attestation under their own signature (§3.2, answered). Never a
     * signature on the soldier's behalf.
     */
    'transfer_override',
    /**
     * Admin/csm completed a plain issue without the soldier's
     * signature, by attestation under their own signature (owner, 2026-09-13,
     * story 25). Never a signature on the soldier's behalf.
     */
    'issue_override',
    /** A platoon sergeant handed gear from their own holdings to a soldier in the platoon (story 30). */
    'distribute',
    /** A soldier returned distributed gear to the platoon sergeant's holding, not to stock (story 30). */
    'distribute_return',
    'rotation_out',
    'rotation_in',
    'report_lost',
    'report_damaged',
    'write_off',
    /**
     * A unit sitting unheld in company stock was flagged damaged, or repaired
     * back to good, directly from the registry (story 43) — no soldier, no
     * assignment. `report_lost`/`report_damaged` stay for the soldier-facing
     * flow; these two are the storeroom-facing equivalent for stock nobody
     * currently holds. Write-off of an unheld unit reuses `write_off` — that
     * type was never assignment-specific.
     */
    'unit_damaged',
    'unit_repaired',
    /**
     * Admin corrected a data-entry mistake on an unheld unit directly — a
     * mistyped serial, the wrong condition (story 43). Deliberately its own
     * type, never disguised as one of the lifecycle events above: nothing
     * actually happened to the item, someone just typed it in wrong the first
     * time.
     */
    'unit_corrected',
    'expend',
    'stock_adjust',
    'user_update',
    'role_change',
    /**
     * Not in REQUIREMENTS §2.4's list, added for audit completeness: creating,
     * renaming, deactivating a unit, or changing who commands it, changes the
     * chain of command over every soldier in it. That belongs in the ledger
     * under its own name, not disguised as a user update.
     */
    'unit_change',
    /** Same reasoning: a catalog edit is its own kind of event. */
    'catalog_change',
    /** A form template created, renamed, re-lined or (de)activated (owner, 2026-09-13). */
    'template_change',
    /** Creating, editing, or closing a mission: the set that overdue is judged against. */
    'mission_change',
]);
/** watch-probe: confirms Vite re-transforms shared dist without a restart. */
export const WATCH_PROBE = 1;
//# sourceMappingURL=transactions.js.map